import type { Feature, Polygon, MultiPolygon, Position } from 'geojson';
import proj4 from 'proj4';

const LPIS_VECTOR_URL =
  'https://mze.gov.cz/public/app/lpisext/lpis/verejny2/vector';
const LPIS_CROP_URL =
  'https://mze.gov.cz/public/app/lpisext/lpis/ng/evidence/rest/gr/plodiny';
const DIRECT_PAYMENT_MEASURE_ID = 201;
const CROP_REGISTER_TIMEOUT_MS = 5000;

const KROVAK_DEFINITION =
  '+proj=krovak +lat_0=49.5 +lon_0=24.8333333333333 ' +
  '+alpha=30.2881397527778 +k=0.9999 +x_0=0 +y_0=0 +ellps=bessel ' +
  // Rotations use the position-vector sign convention that proj4 +towgs84
  // expects. The published EPSG:5514 values are coordinate-frame (negated).
  '+towgs84=572.213,85.334,461.94,4.9732,1.529,5.2484,3.5378 ' +
  '+units=m +no_defs +type=crs';

const KROVAK_X_MIN = -950000;
const KROVAK_X_MAX = -400000;
const KROVAK_Y_MIN = -1250000;
const KROVAK_Y_MAX = -900000;

export const LPIS_POLYGON_ZOOM = 15;

export interface LpisBounds {
  west: number;
  south: number;
  east: number;
  north: number;
}

export interface LpisFeatureProperties {
  lpis: true;
  id: number;
  candidateIds: number[];
  code: string;
  userName: string | null;
  userId: number | null;
  areaHa: number | null;
}

export type LpisFeature = Feature<Polygon | MultiPolygon, LpisFeatureProperties>;

interface LpisVectorFeature {
  type?: string;
  geometry?: {
    type?: string;
    coordinates?: unknown;
  };
  properties?: {
    IDZ?: unknown;
    ZID?: unknown;
    TOOLTIP?: unknown;
  };
}

interface LpisCropRegisterPayload {
  success?: unknown;
  rows?: Array<{ plodinaNazev?: unknown }>;
}

export interface LpisCropQuery {
  code?: string | null;
  parcelId?: number | null;
}

interface LpisVectorResponse {
  features?: LpisVectorFeature[];
}

export interface LpisTooltip {
  code: string;
  areaHa: number | null;
  userName: string | null;
  userId: number | null;
}

export function padLpisBounds(
  bounds: LpisBounds,
  factor: number = 0.1
): LpisBounds {
  const width = bounds.east - bounds.west;
  const height = bounds.north - bounds.south;

  return {
    west: bounds.west - width * factor,
    south: bounds.south - height * factor,
    east: bounds.east + width * factor,
    north: bounds.north + height * factor,
  };
}

export function containsLpisBounds(outer: LpisBounds, inner: LpisBounds) {
  return (
    outer.west <= inner.west &&
    outer.south <= inner.south &&
    outer.east >= inner.east &&
    outer.north >= inner.north
  );
}

export function parseLpisTooltip(tooltip: string): LpisTooltip {
  const text = tooltip.replace(/<br\s*\/?>/gi, '\n').trim();
  const lines = text
    .split('\n')
    .map((line) => line.trim())
    .filter(Boolean);
  const header = lines[0] || '';
  const headerMatch = header.match(/^(\S+)(?:\s+([\d,]+)\s*ha)?/i);
  const userIdMatch = text.match(/ID:\s*(\d+)/i);
  const userName = lines.find(
    (line) => line.length > 0 && !/^ID:/i.test(line) && line !== header
  );

  return {
    code: headerMatch?.[1] || header || 'LPIS',
    areaHa: parseAreaHa(headerMatch?.[2]),
    userName: userName || null,
    userId: parsePositiveInt(userIdMatch?.[1]),
  };
}

export async function fetchLpisPolygons(
  bounds: LpisBounds,
  signal: AbortSignal
): Promise<LpisFeature[]> {
  const payload = await fetchLpisVector('FB_PLAT_PLG', bounds, signal);
  const features: LpisFeature[] = [];

  for (const feature of payload) {
    const parsed = readLpisFeature(feature);

    if (parsed) {
      features.push(parsed);
    }
  }

  return features;
}

export async function fetchLpisDeclaredCrops(
  geometry: Polygon | MultiPolygon,
  signal: AbortSignal,
  query: LpisCropQuery = {}
): Promise<string | null> {
  const parcelId =
    parsePositiveInt(query.parcelId) ||
    (await resolveCurrentParcelId(geometry, query.code, signal));

  if (parcelId === null || signal.aborted) {
    return null;
  }

  const names = await fetchCampaignCropNames(
    parcelId,
    new Date().getFullYear(),
    signal
  );

  if (!names || names.length === 0) {
    return null;
  }

  return names.join(', ');
}

async function fetchLpisVector(
  layers: string,
  bounds: LpisBounds,
  signal: AbortSignal
): Promise<LpisVectorFeature[]> {
  const krovakBbox = wgs84ToKrovakBbox(bounds);
  const body = new URLSearchParams({
    LAYERS: layers,
    t: String(Date.now()),
    bbox: [
      krovakBbox.minX,
      krovakBbox.minY,
      krovakBbox.maxX,
      krovakBbox.maxY,
    ].join(','),
  });
  const response = await fetch(LPIS_VECTOR_URL, {
    method: 'POST',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/x-www-form-urlencoded',
    },
    body,
    signal,
  });

  if (!response.ok) {
    throw new Error(`LPIS request failed (${response.status}).`);
  }

  const payload: LpisVectorResponse = await response.json();

  if (!Array.isArray(payload?.features)) {
    throw new Error('LPIS service returned invalid polygons.');
  }

  return payload.features;
}

async function resolveCurrentParcelId(
  geometry: Polygon | MultiPolygon,
  code: string | null | undefined,
  signal: AbortSignal
): Promise<number | null> {
  const expectedCode = readCropString(code);

  if (!expectedCode) {
    return null;
  }

  const payload = await fetchLpisVector(
    'FB_PLAT_PLG',
    padLpisBounds(boundsFromGeometry(geometry), 0.05),
    signal
  );

  for (const feature of payload) {
    const tooltip = parseLpisTooltip(String(feature.properties?.TOOLTIP || ''));

    if (!codesMatch(tooltip.code, expectedCode)) {
      continue;
    }

    const parcelId = parsePositiveInt(feature.properties?.ZID);

    if (parcelId) {
      return parcelId;
    }
  }

  return null;
}

async function fetchCampaignCropNames(
  parcelId: number,
  year: number,
  signal: AbortSignal
): Promise<string[] | null> {
  const url = `${LPIS_CROP_URL}?rok=${year}&fbId=${parcelId}&opatreniId=${DIRECT_PAYMENT_MEASURE_ID}&page=1&start=0&limit=25`;

  return cropNamesFromRegister(
    await fetchLpisJson<LpisCropRegisterPayload>(url, signal)
  );
}

function cropNamesFromRegister(payload: LpisCropRegisterPayload | null) {
  if (!payload || payload.success !== true || !Array.isArray(payload.rows)) {
    return null;
  }

  return uniqueNames(
    payload.rows
      .map((row) => readCropString(row.plodinaNazev))
      .filter((name): name is string => Boolean(name))
  );
}

async function fetchLpisJson<T>(
  url: string,
  signal: AbortSignal
): Promise<T | null> {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), CROP_REGISTER_TIMEOUT_MS);
  const handleAbort = () => controller.abort();

  signal.addEventListener('abort', handleAbort);

  try {
    const response = await fetch(url, {
      signal: controller.signal,
      redirect: 'error',
    });

    if (!response.ok) {
      return null;
    }

    const text = (await response.text()).trim();

    if (!text.startsWith('{') && !text.startsWith('[')) {
      return null;
    }

    return JSON.parse(text) as T;
  } catch {
    return null;
  } finally {
    signal.removeEventListener('abort', handleAbort);
    clearTimeout(timer);
  }
}

function parseAreaHa(value: string | undefined) {
  if (!value) {
    return null;
  }

  const areaHa = Number(value.replace(',', '.'));

  return Number.isFinite(areaHa) ? areaHa : null;
}

function parsePositiveInt(value: unknown) {
  const parsed = typeof value === 'number' ? value : Number(value);

  if (!Number.isSafeInteger(parsed) || parsed <= 0) {
    return null;
  }

  return parsed;
}

function parseCoordinate(value: unknown) {
  const parsed = typeof value === 'number' ? value : Number(value);

  return Number.isFinite(parsed) ? parsed : null;
}

function isKrovakPoint(x: number, y: number) {
  return (
    x >= KROVAK_X_MIN &&
    x <= KROVAK_X_MAX &&
    y >= KROVAK_Y_MIN &&
    y <= KROVAK_Y_MAX
  );
}

function wgs84ToKrovakBbox(bounds: LpisBounds) {
  const corners: Array<[number, number]> = [
    [bounds.west, bounds.south],
    [bounds.west, bounds.north],
    [bounds.east, bounds.south],
    [bounds.east, bounds.north],
  ];
  const projected = corners.map(
    ([longitude, latitude]) =>
      proj4('EPSG:4326', KROVAK_DEFINITION, [longitude, latitude]) as [
        number,
        number
      ]
  );
  const xs = projected.map(([x]) => x);
  const ys = projected.map(([, y]) => y);

  return {
    minX: Math.min(...xs),
    minY: Math.min(...ys),
    maxX: Math.max(...xs),
    maxY: Math.max(...ys),
  };
}

function krovakToWgs84(x: number, y: number): Position {
  return proj4(KROVAK_DEFINITION, 'EPSG:4326', [x, y]) as Position;
}

function reprojectRing(ring: unknown): Position[] | null {
  if (!Array.isArray(ring) || ring.length < 4) {
    return null;
  }

  const positions: Position[] = [];

  for (const point of ring) {
    if (!Array.isArray(point) || point.length < 2) {
      return null;
    }

    const x = parseCoordinate(point[0]);
    const y = parseCoordinate(point[1]);

    if (x === null || y === null || !isKrovakPoint(x, y)) {
      return null;
    }

    positions.push(krovakToWgs84(x, y));
  }

  return positions;
}

function reprojectPolygon(coordinates: unknown): Position[][] | null {
  if (!Array.isArray(coordinates) || coordinates.length === 0) {
    return null;
  }

  const rings: Position[][] = [];

  for (const ring of coordinates) {
    const projected = reprojectRing(ring);

    if (!projected) {
      return null;
    }

    rings.push(projected);
  }

  return rings;
}

function reprojectGeometry(
  geometry: LpisVectorFeature['geometry']
): Polygon | MultiPolygon | null {
  if (geometry?.type === 'Polygon') {
    const coordinates = reprojectPolygon(geometry.coordinates);

    if (!coordinates) {
      return null;
    }

    return { type: 'Polygon', coordinates };
  }

  if (geometry?.type === 'MultiPolygon') {
    if (!Array.isArray(geometry.coordinates)) {
      return null;
    }

    const polygons: Position[][][] = [];

    for (const polygon of geometry.coordinates) {
      const coordinates = reprojectPolygon(polygon);

      if (!coordinates) {
        return null;
      }

      polygons.push(coordinates);
    }

    if (polygons.length === 0) {
      return null;
    }

    return { type: 'MultiPolygon', coordinates: polygons };
  }

  return null;
}

function uniqueNames(values: string[]) {
  return [...new Set(values)];
}

function codesMatch(left: string | null, right: string) {
  return left?.toLowerCase() === right.toLowerCase();
}

function uniquePositiveIds(values: Array<number | null>) {
  return [...new Set(values.filter((value): value is number => value !== null))];
}

function geometryPositions(geometry: Polygon | MultiPolygon): Position[] {
  const rings =
    geometry.type === 'Polygon'
      ? geometry.coordinates
      : geometry.coordinates.flat();

  return rings.flat();
}

function boundsFromGeometry(geometry: Polygon | MultiPolygon): LpisBounds {
  const positions = geometryPositions(geometry);
  const longitudes = positions.map((position) => position[0]);
  const latitudes = positions.map((position) => position[1]);

  return {
    west: Math.min(...longitudes),
    south: Math.min(...latitudes),
    east: Math.max(...longitudes),
    north: Math.max(...latitudes),
  };
}

function readCropString(value: unknown) {
  if (typeof value !== 'string') {
    return null;
  }

  const trimmed = value.trim();

  return trimmed.length > 0 ? trimmed : null;
}

function readLpisFeature(feature: LpisVectorFeature): LpisFeature | null {
  if (feature?.type !== 'Feature') {
    return null;
  }

  const geometry = reprojectGeometry(feature.geometry);

  if (!geometry) {
    return null;
  }

  const tooltip = parseLpisTooltip(String(feature.properties?.TOOLTIP || ''));
  const candidateIds = uniquePositiveIds([
    parsePositiveInt(feature.properties?.ZID),
    parsePositiveInt(feature.properties?.IDZ),
  ]);

  if (!tooltip.code || candidateIds.length === 0) {
    return null;
  }

  return {
    type: 'Feature',
    id: candidateIds[0],
    properties: {
      lpis: true,
      id: candidateIds[0],
      candidateIds,
      code: tooltip.code,
      userName: tooltip.userName,
      userId: tooltip.userId,
      areaHa: tooltip.areaHa,
    },
    geometry,
  };
}
